import React, { useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import "./business-playwright.css";
import { ASSETS, BUSINESS_POSTERS, PUBLIC_POSTERS, SECTION_IDS } from "./config";
import { usePageSeo } from "./useSeo";
import { scrollToSection } from "./scroll";
import { Header, BookACallLink } from "./Header";
import { Hero } from "./Hero";
import { SplitScene } from "./SplitScene";
import { PosterGallery } from "./PosterGallery";
import { FloatingCta, Footer } from "./FooterAndFloating";
import { TicketAlertsForm } from "./TicketAlertsForm";

const ProductionPhoto = () => (
  <figure className="bp-photo">
    <img
      src={ASSETS.productionPhoto}
      alt="Ricky Oakley and Kirsten Callaghan in the 2026 production of While We Wait"
      loading="lazy"
      decoding="async"
      width="2000"
      height="1333"
      data-testid="production-photo"
    />
    <figcaption className="bp-caption" data-testid="production-photo-caption">
      Ricky Oakley and Kirsten Callaghan in our 2026 production of While We Wait
    </figcaption>
  </figure>
);

export default function BusinessPlaywrightHome() {
  usePageSeo();
  const heroRef = useRef(null);
  const { hash } = useLocation();

  useEffect(() => {
    const id = hash.replace("#", "");
    if (id) requestAnimationFrame(() => scrollToSection(id));
    else window.scrollTo(0, 0);
  }, [hash]);

  return (
    <div className="bp-site" data-testid="business-playwright-home">
      <Header />
      <main id="main">
        <Hero heroRef={heroRef} />

        <SplitScene id={SECTION_IDS.businessStories} aside={<ProductionPhoto />} testId="section-business-stories">
          <p>Your clients want to understand your story and your mission.</p>
          <p>But in this world of endless online content, it’s harder than ever to stand out.</p>
          <p>That’s why we work with mission-driven businesses and exceptional founders to turn their stories into professionally-produced stage plays.</p>
          <p>We also create training and team building experiences that your colleagues will remember for years to come.</p>
          <BookACallLink testId="section-1-book-a-call" />
        </SplitScene>

        <PosterGallery
          id={SECTION_IDS.businessProductions}
          heading="Current productions - for businesses"
          subheading="Book these for your workplace now"
          posters={BUSINESS_POSTERS}
          testId="section-business-productions"
        />

        <SplitScene
          id={SECTION_IDS.attention}
          asideClassName="bp-split-aside--statement"
          aside={<p className="bp-pullout" data-testid="pullout-statement">Exceptional in-person storytelling</p>}
          testId="section-attention"
        >
          <p>Each day more than 700,000 hours of video are uploaded to YouTube. And around 2 million posts are shared on LinkedIn.</p>
          <p>Even the good stuff gets drowned out and forgotten.</p>
          <p>And online training can evaporate the moment the browser is closed.</p>
          <p>The answer is exceptional, live, in-person storytelling.</p>
          <p>The answer is The Business Playwright.</p>
        </SplitScene>

        <PosterGallery
          id={SECTION_IDS.publicProductions}
          heading="Current productions - for the general public"
          subheading="Sign up for ticket alerts now"
          posters={PUBLIC_POSTERS}
          testId="section-public-productions"
        >
          <TicketAlertsForm />
        </PosterGallery>

        <SplitScene
          id={SECTION_IDS.about}
          asideClassName="bp-split-aside--statement"
          aside={
            <div className="bp-founder" data-testid="about-founder">
              <Link to="/about" className="bp-founder-link" data-testid="about-portrait-link">
                <img src={ASSETS.craigPortrait} alt="Craig “Doe” Wilmann, founder of The Business Playwright" width="1254" height="1254" loading="lazy" decoding="async" className="bp-founder-portrait" data-testid="about-portrait" />
              </Link>
              <p className="bp-caption bp-founder-caption" data-testid="about-portrait-caption">The Business Playwright founder, Craig “Doe” Wilmann</p>
              <div className="bp-statement bp-statement--small" data-testid="about-statement">
                <p>Theatre for business.</p>
                <p>Theatre about business.</p>
                <p>Stories you’ll remember for the rest of your life.</p>
              </div>
            </div>
          }
          testId="section-about"
        >
          <p>Theatre survived Cromwell and the Blitz. It will survive - and boom - in the AI era too.</p>
          <p>Imagine your ideal clients sat in a dark room, phones off, watching a piece of drama or comedy that exemplifies what your business is all about.</p>
          <p>Imagine your colleagues fully engaged with the most memorable training day they’ve ever experienced.</p>
          <p>Imagine your business - in the AI era - using live storytelling to set yourselves apart.</p>
        </SplitScene>

        <section id={SECTION_IDS.bookACall} className="bp-scene bp-final" data-testid="section-final-cta">
          <div className="bp-wrap bp-final-inner">
            <p className="bp-final-lead">This new technology is only getting more powerful.</p>
            <p className="bp-final-lead">There’s no time to waste.</p>
            <p>If you’d like to discuss how your business can thrive in this new world of fast-moving theatre, book a call now.</p>
            <BookACallLink testId="final-book-a-call" />
          </div>
        </section>
      </main>
      <Footer />
      <FloatingCta heroRef={heroRef} />
    </div>
  );
}
